import { useMutation, useQueryClient, useQuery } from "react-query";
import { apiPost, apiPut, apiGet } from "@shared/queryHooks/api";

/* API request functions
--===================================================-- */

const getBoardWhatJobsListings = async (jobId: number) => {
  return await apiGet({
    path: `/jobs/${jobId}/board_what_jobs_listings`,
  });
};

const createWhatJobsCheckoutSession = async ({
  jobId,
  boardWhatJobsListing,
}: {
  jobId: number;
  boardWhatJobsListing: any;
}) => {
  return await apiPost({
    path: `/jobs/${jobId}/board_what_jobs_listings/create_checkout_session`,
    variables: { boardWhatJobsListing },
  });
};

const saveBoardWhatJobsListingDraft = async ({ jobId, ...boardWhatJobsListing }) => {
  window.logger(
    "%c[useWhatJobsListing] saveBoardWhatJobsListingDraft\n\n\n\n",
    "background-color: #FF76D2",
    {
      boardWhatJobsListing,
    },
  );
  return await apiPost({
    path: `/jobs/${jobId}/board_what_jobs_listings/save_draft`,
    variables: { boardWhatJobsListing },
  });
};

const updateActiveBoardWhatJobsListing = async ({ jobId, id, ...boardWhatJobsListing }) => {
  window.logger(
    "%c[useWhatJobsListing] updateActiveBoardWhatJobsListing\n\n\n\n",
    "background-color: #FF76D2",
    {
      boardWhatJobsListing,
    },
  );
  return await apiPut({
    path: `/jobs/${jobId}/board_what_jobs_listings/${id}/update_active`,
    variables: { boardWhatJobsListing },
  });
};

const createPaidBoardWhatJobsListing = async ({ jobId, boardWhatJobsListing }) => {
  window.logger(
    "%c[useWhatJobsListing] createPaidBoardWhatJobsListing\n\n\n\n",
    "background-color: #FF76D2",
    {
      boardWhatJobsListing,
    },
  );
  return await apiPost({
    path: `/jobs/${jobId}/board_what_jobs_listings/create_paid_listing`,
    variables: { boardWhatJobsListing },
  });
};

const pauseBoardWhatJobsListing = async ({ jobId, id }) => {
  window.logger(
    "%c[useWhatJobsListing] pauseBoardWhatJobsListing\n\n\n\n",
    "background-color: #FF76D2",
    {
      jobId,
      id,
    },
  );
  return await apiPost({
    path: `/jobs/${jobId}/board_what_jobs_listings/${id}/pause`,
    variables: {},
  });
};

const activateBoardWhatJobsListing = async ({ jobId, id }) => {
  window.logger(
    "%c[useWhatJobsListing] activateBoardWhatJobsListing\n\n\n\n",
    "background-color: #FF76D2",
    {
      jobId,
      id,
    },
  );
  return await apiPost({
    path: `/jobs/${jobId}/board_what_jobs_listings/${id}/activate`,
    variables: {},
  });
};

/* Hooks
--===================================================-- */

function useBoardWhatJobsListings(jobId: number) {
  return useQuery(["boardWhatJobsListings", jobId], () => getBoardWhatJobsListings(jobId), {
    enabled: !!jobId,
  });
}

function useCreateWhatJobsCheckoutSession() {
  return useMutation(createWhatJobsCheckoutSession);
}

function useSaveBoardWhatJobsListingDraft() {
  const queryClient = useQueryClient();
  return useMutation(saveBoardWhatJobsListingDraft, {
    onSuccess: (data, mutationVariables) => {
      // queryClient.invalidateQueries(["jobs", mutationVariables.jobId]);
      queryClient.invalidateQueries(["boardWhatJobsListings", mutationVariables.jobId]);
    },
  });
}

function useUpdateActiveBoardWhatJobsListing() {
  const queryClient = useQueryClient();
  return useMutation(updateActiveBoardWhatJobsListing, {
    onSuccess: (data, mutationVariables) => {
      // queryClient.invalidateQueries(["jobs", mutationVariables.jobId]);
      queryClient.invalidateQueries(["boardWhatJobsListings", mutationVariables.jobId]);
    },
  });
}

function useCreatePaidBoardWhatJobsListing() {
  const queryClient = useQueryClient();
  return useMutation(createPaidBoardWhatJobsListing, {
    onSuccess: (data, mutationVariables) => {
      // queryClient.invalidateQueries(["jobs", mutationVariables.jobId]);
      queryClient.invalidateQueries(["boardWhatJobsListings", mutationVariables.jobId]);
    },
  });
}

function usePauseBoardWhatJobsListing() {
  const queryClient = useQueryClient();
  return useMutation(pauseBoardWhatJobsListing, {
    onSuccess: (data, mutationVariables) => {
      // queryClient.invalidateQueries(["jobs", mutationVariables.jobId]);
      queryClient.invalidateQueries(["boardWhatJobsListings", mutationVariables.jobId]);
    },
  });
}

function useActivateBoardWhatJobsListing() {
  const queryClient = useQueryClient();
  return useMutation(activateBoardWhatJobsListing, {
    onSuccess: (data, mutationVariables) => {
      // queryClient.invalidateQueries(["jobs", mutationVariables.jobId]);
      queryClient.invalidateQueries(["boardWhatJobsListings", mutationVariables.jobId]);
    },
  });
}

export {
  useBoardWhatJobsListings,
  useCreateWhatJobsCheckoutSession,
  useSaveBoardWhatJobsListingDraft,
  useCreatePaidBoardWhatJobsListing,
  useUpdateActiveBoardWhatJobsListing,
  usePauseBoardWhatJobsListing,
  useActivateBoardWhatJobsListing,
};
